import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.jsx';
import { useToast, Field, Progress } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';
import AuthShell, { AuthHead } from '../components/AuthShell.jsx';
import LocationFields from '../components/LocationFields.jsx';
import PhoneField from '../components/PhoneField.jsx';
import { isValidPhone } from '../lib/validation.js';
import { firstName } from '../lib/format.js';

export default function RegisterPatient() {
  const { register } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    lat: null,
    lng: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Mirrors the server's mandatory set so the button is only enabled when the
  // request will actually succeed. Location must be pinned on the map.
  const checks = [
    !!form.name.trim(),
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email),
    form.password.length >= 6,
    isValidPhone(form.phone),
    !!form.gender,
    !!form.state,
    !!form.city,
    form.lat != null,
  ];
  const complete = checks.every(Boolean);
  const done = checks.filter(Boolean).length;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const user = await register({ ...form, role: 'patient' });
      toast.success(`Welcome to HealAtHome, ${firstName(user.name)}!`);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <AuthShell back={{ to: '/register' }} wide>
      <AuthHead title="Create your account" subtitle="So we can match you with nurses who cover your area." />

      {/* A visible completeness meter turns a long form into a short journey. */}
      <div style={{ maxWidth: 420, margin: '0 auto var(--s-6)' }}>
        <div className="row-between mb-1">
          <span className="t-label">Profile completeness</span>
          <span className="tiny dim">
            {done} of {checks.length}
          </span>
        </div>
        <Progress value={done} max={checks.length} label="Registration progress" />
      </div>

      {error && (
        <div className="banner banner-danger">
          <Icon name="warning" />
          <span className="grow">{error}</span>
        </div>
      )}

      <form onSubmit={submit}>
        <div className="form-section">
          <span className="form-section-num">1</span>
          <h2>Your details</h2>
        </div>

        <div className="auth-form-grid">
          <Field label="Full name" id="name" required>
            <input id="name" type="text" value={form.name} onChange={set('name')} required autoComplete="name" placeholder="Anjali Menon" />
          </Field>

          <Field label="Email address" id="email" required>
            <input id="email" type="email" value={form.email} onChange={set('email')} required autoComplete="email" placeholder="you@example.com" />
          </Field>

          <Field label="Password" id="pw" hint="At least 6 characters." required>
            <div className="input-icon">
              <Icon name="lock" />
              <input
                id="pw"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={set('password')}
                required
                minLength={6}
                autoComplete="new-password"
                style={{ paddingRight: 46 }}
              />
              <button
                type="button"
                className="input-clear"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <Icon name="eye" />
              </button>
            </div>
          </Field>

          <PhoneField value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} required />

          <Field label="Gender" id="gender" required>
            <select id="gender" value={form.gender} onChange={set('gender')} required>
              <option value="">Select…</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="other">Other</option>
            </select>
          </Field>
        </div>

        <div className="form-section">
          <span className="form-section-num">2</span>
          <h2>Where you need care</h2>
        </div>

        <LocationFields
          value={{ state: form.state, city: form.city, address: form.address, lat: form.lat, lng: form.lng }}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
        />

        {/* The action stays a readable width even though the form is wide. */}
        <div style={{ maxWidth: 420, margin: 'var(--s-6) auto 0' }}>
          <button className="btn btn-primary btn-lg btn-block" disabled={busy || !complete}>
            {busy ? <span className="spinner" /> : 'Create account'}
          </button>
          {!complete && !busy && (
            <p className="tiny dim center mt-1">Fill in every required field to continue.</p>
          )}
        </div>
      </form>

      <p className="auth-foot">
        Are you a nurse? <Link to="/register/nurse">Apply here</Link> · Already registered?{' '}
        <Link to="/login">Sign in</Link>
      </p>
    </AuthShell>
  );
}
